"use client";

import { FaceLandmarker, type NormalizedLandmark } from "@mediapipe/tasks-vision";
import { useEffect, useRef, useState } from "react";
import { logger } from "@/core/logging";
import { MEDIAPIPE_FACE_LANDMARKER_MODEL_URL, prefersMediapipeCpuDelegate, resolveVisionTasksFileset } from "./mediapipeConfig";

/** MediaPipe Face Mesh eyelid/corner indices used to locate the eye midpoint. */
const LM = {
  LEFT_EYE_UPPER: 159,
  LEFT_EYE_LOWER: 145,
  RIGHT_EYE_UPPER: 386,
  RIGHT_EYE_LOWER: 374,
  LEFT_EYE_OUTER: 33,
  RIGHT_EYE_OUTER: 263
} as const;

/** Eyes typically sit in the upper-middle of a 3:4 face crop; used until detection resolves. */
export const EYE_ZOOM_ORIGIN_FALLBACK = "50% 38%";

/**
 * Runs one-shot MediaPipe Face Landmarker inference on an image URL and returns a CSS
 * `transform-origin` string centered on the midpoint of the eyes. Used to zoom the lash/brow
 * transformation comparison in on the eyes. Fails silently to a sensible fallback.
 */
export function useEyeZoomOrigin(imageUrl: string | undefined): {
  origin: string;
  isDetecting: boolean;
} {
  const [origin, setOrigin] = useState<string>(EYE_ZOOM_ORIGIN_FALLBACK);
  const [isDetecting, setIsDetecting] = useState(false);
  const landmarkerRef = useRef<FaceLandmarker | null>(null);
  const lastImageUrlRef = useRef<string | null>(null);
  useEffect(() => {
    if (!imageUrl || lastImageUrlRef.current === imageUrl) return;
    const ctrl = {
      cancelled: false
    };
    const run = async () => {
      setIsDetecting(true);
      try {
        if (!landmarkerRef.current) {
          const vision = await resolveVisionTasksFileset();
          function createEyeLandmarker(delegate: "GPU" | "CPU") {
            return FaceLandmarker.createFromOptions(vision, {
              baseOptions: {
                modelAssetPath: MEDIAPIPE_FACE_LANDMARKER_MODEL_URL,
                delegate
              },
              runningMode: "IMAGE",
              numFaces: 1,
              minFaceDetectionConfidence: 0.5,
              minFacePresenceConfidence: 0.5,
              minTrackingConfidence: 0.5,
              outputFaceBlendshapes: false,
              outputFacialTransformationMatrixes: false
            });
          }
          if (prefersMediapipeCpuDelegate()) {
            landmarkerRef.current = await createEyeLandmarker("CPU");
          } else {
            try {
              landmarkerRef.current = await createEyeLandmarker("GPU");
            } catch (gpuErr) {
              logger.warn("eye_zoom_landmarker_gpu_failed_falling_back_to_cpu", {
                error: gpuErr instanceof Error ? gpuErr.message : String(gpuErr)
              });
              landmarkerRef.current = await createEyeLandmarker("CPU");
            }
          }
        }
        if (ctrl.cancelled) return;
        const img = await new Promise<HTMLImageElement>((resolve, reject) => {
          const el = new Image();
          el.crossOrigin = "anonymous";
          el.onload = () => resolve(el);
          el.onerror = () => reject(new Error("image_load_failed"));
          el.src = imageUrl;
        });

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- cleanup may cancel between awaits
        if (ctrl.cancelled) return;
        const result = landmarkerRef.current.detect(img);
        if (!result.faceLandmarks.length) return;
        const lm: NormalizedLandmark[] = result.faceLandmarks[0];
        const leftEyeY = (lm[LM.LEFT_EYE_UPPER].y + lm[LM.LEFT_EYE_LOWER].y) / 2;
        const rightEyeY = (lm[LM.RIGHT_EYE_UPPER].y + lm[LM.RIGHT_EYE_LOWER].y) / 2;
        const eyeY = (leftEyeY + rightEyeY) / 2;
        const eyeX = (lm[LM.LEFT_EYE_OUTER].x + lm[LM.RIGHT_EYE_OUTER].x) / 2;
        lastImageUrlRef.current = imageUrl;
        setOrigin(`${clampPercent(eyeX * 100).toFixed(1)}% ${clampPercent(eyeY * 100).toFixed(1)}%`);
      } catch (err) {
        logger.error("eye_zoom_landmark_detection_failed", undefined, err as Error);
      } finally {
        // Guard the cancelled path: after unmount this setState is a no-op in
        // the browser but throws in vitest once jsdom is torn down (react-dom
        // reads `window`), turning the dangling run() promise into an
        // unhandled rejection that fails the suite.
        if (!ctrl.cancelled) {
          setIsDetecting(false);
        }
      }
    };
    void run();
    return () => {
      ctrl.cancelled = true;
      if (landmarkerRef.current) {
        landmarkerRef.current.close();
        landmarkerRef.current = null;
      }
    };
  }, [imageUrl]);
  return {
    origin,
    isDetecting
  };
}
function clampPercent(value: number): number {
  return Math.max(0, Math.min(100, value));
}